/**
 * Website Settings Page (DRR-107)
 *
 * Admin page for editing the tenant's public website data.
 * Data feeds the signed manifest consumed by aws-website-generation.
 *
 * ARCHITECTURE:
 *   TenantBranding (DynamoDB) → GraphQL → this page
 *   This page → updateTenantBranding mutation → DynamoDB
 *   export script → reads TenantBranding → signed manifest → website repo
 *
 * FERP Principles:
 * - Repository pattern: Data through GraphQL (useTenantBranding + UPDATE_TENANT_BRANDING)
 * - Mantine v8.x: All UI components
 * - Admin-only: Requires website:settings:update scope
 */

import React, { useEffect, useState } from 'react';
import {
  Container,
  Title,
  Text,
  Stack,
  Paper,
  TextInput,
  Textarea,
  TagsInput,
  Button,
  Group,
  Alert,
  Loader,
  Center,
  Divider,
  Badge,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconWorld,
  IconDeviceFloppy,
  IconCheck,
  IconAlertTriangle,
  IconShieldLock,
} from '@tabler/icons-react';
import { useMutation } from '@apollo/client/react';
import { useTenantId } from '@/auth';
import { useTenantBranding } from '@/hooks/useTenantBranding';
import { UPDATE_TENANT_BRANDING } from '@ferp/graphql-client/src/tenantBrandingQueries';

interface WebsiteSettingsFormValues {
  websiteSlug: string;
  companyName: string;
  companyTagline: string;
  supportPhone: string;
  supportEmail: string;
  address: string;
  hours: string;
  licenseType: string;
  services: string[];
  accreditations: string[];
}

export function WebsiteSettingsPage(): React.ReactElement {
  const tenantId = useTenantId();
  const { branding, loading, error, refetch } = useTenantBranding(tenantId);
  const [saving, setSaving] = useState(false);

  const [updateBranding] = useMutation(UPDATE_TENANT_BRANDING);

  const form = useForm<WebsiteSettingsFormValues>({
    initialValues: {
      websiteSlug: '',
      companyName: '',
      companyTagline: '',
      supportPhone: '',
      supportEmail: '',
      address: '',
      hours: '',
      licenseType: '',
      services: [],
      accreditations: [],
    },
    validate: {
      // Slug format validation is enforced by backend Zen Engine (DRR-055)
      companyName: (v) => (!v ? 'Company name is required' : null),
    },
  });

  // Populate form when branding data loads
  useEffect(() => {
    if (branding) {
      form.setValues({
        websiteSlug: branding.websiteSlug ?? '',
        companyName: branding.companyName ?? '',
        companyTagline: branding.companyTagline ?? '',
        supportPhone: branding.supportPhone ?? '',
        supportEmail: branding.supportEmail ?? '',
        address: branding.address ?? '',
        hours: branding.hours ?? '',
        licenseType: branding.licenseType ?? '',
        services: branding.services ?? [],
        accreditations: branding.accreditations ?? [],
      });
      form.resetDirty();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branding]);

  const handleSubmit = async (values: WebsiteSettingsFormValues) => {
    if (!tenantId) return;

    setSaving(true);
    try {
      await updateBranding({
        variables: {
          input: {
            tenantId,
            companyName: values.companyName,
            companyTagline: values.companyTagline || null,
            supportPhone: values.supportPhone || null,
            supportEmail: values.supportEmail || null,
            websiteSlug: values.websiteSlug || null,
            address: values.address || null,
            hours: values.hours || null,
            licenseType: values.licenseType || null,
            services: values.services.length > 0 ? values.services : null,
            accreditations:
              values.accreditations.length > 0 ? values.accreditations : null,
          },
        },
      });

      notifications.show({
        title: 'Settings saved',
        message: 'Public website data updated. Export manifest to publish.',
        color: 'green',
        icon: <IconCheck size={16} />,
      });

      form.resetDirty();
      await refetch();
    } catch (err) {
      notifications.show({
        title: 'Save failed',
        message:
          err instanceof Error ? err.message : 'Failed to update settings',
        color: 'red',
        icon: <IconAlertTriangle size={16} />,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container size="md" py="xl">
        <Center h={400}>
          <Stack ta="center" gap="md">
            <Loader size="lg" />
            <Text c="dimmed">Loading website settings...</Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="md" py="xl">
        <Alert color="red" title="Error" icon={<IconAlertTriangle />}>
          Failed to load website settings: {error.message}
        </Alert>
      </Container>
    );
  }

  if (!branding) {
    return (
      <Container size="md" py="xl">
        <Alert color="yellow" title="No Branding" icon={<IconWorld />}>
          Tenant branding has not been configured yet. Contact your
          administrator.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Website Settings</Title>
            <Text c="dimmed" size="sm">
              Public website data for your organization
            </Text>
          </div>
          {branding.updatedAt && (
            <Badge variant="light" color="gray" size="sm">
              Last updated: {new Date(branding.updatedAt).toLocaleDateString()}
            </Badge>
          )}
        </Group>

        {/* Info Banner */}
        <Alert
          color="blue"
          variant="light"
          icon={<IconShieldLock size={18} />}
        >
          <Text size="sm">
            These fields are exported to your public website via a{' '}
            <strong>signed manifest</strong>. Changes here are
            cryptographically verified before your website uses them. No one
            outside FERP can modify this data.
          </Text>
        </Alert>

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="lg">
            {/* Identity */}
            <Paper withBorder p="md">
              <Text fw={600} mb="md">
                Identity
              </Text>
              <Stack gap="sm">
                <TextInput
                  label="Organization Name"
                  required
                  {...form.getInputProps('companyName')}
                />
                <TextInput
                  label="Tagline"
                  description="Short description shown on the website"
                  {...form.getInputProps('companyTagline')}
                />
                <TextInput
                  label="Website Slug"
                  description="URL-safe identifier (e.g., hillside-harmony)"
                  {...form.getInputProps('websiteSlug')}
                />
              </Stack>
            </Paper>

            {/* Contact */}
            <Paper withBorder p="md">
              <Text fw={600} mb="md">
                Contact Information
              </Text>
              <Stack gap="sm">
                <TextInput
                  label="Phone"
                  {...form.getInputProps('supportPhone')}
                />
                <TextInput
                  label="Email"
                  {...form.getInputProps('supportEmail')}
                />
                <Textarea
                  label="Address"
                  autosize
                  minRows={2}
                  {...form.getInputProps('address')}
                />
                <TextInput
                  label="Hours"
                  description="e.g., Visiting hours 8:00 AM - 8:00 PM daily"
                  {...form.getInputProps('hours')}
                />
              </Stack>
            </Paper>

            {/* Licensing */}
            <Paper withBorder p="md">
              <Text fw={600} mb="md">
                Licensing
              </Text>
              <TextInput
                label="License Type"
                description="e.g., MN 144G Assisted Living"
                {...form.getInputProps('licenseType')}
              />
            </Paper>

            {/* Services */}
            <Paper withBorder p="md">
              <Text fw={600} mb="md">
                Services Offered
              </Text>
              <TagsInput
                label="Services"
                description="Press Enter to add a service"
                placeholder="Add a service..."
                {...form.getInputProps('services')}
              />
            </Paper>

            {/* Accreditations */}
            <Paper withBorder p="md">
              <Text fw={600} mb="md">
                Accreditations
              </Text>
              <TagsInput
                label="Accreditations & Certifications"
                description="Press Enter to add"
                placeholder="Add an accreditation..."
                {...form.getInputProps('accreditations')}
              />
            </Paper>

            <Divider />

            {/* Save */}
            <Group justify="flex-end">
              <Button
                type="submit"
                leftSection={<IconDeviceFloppy size={16} />}
                loading={saving}
                disabled={!form.isDirty()}
              >
                Save Settings
              </Button>
            </Group>
          </Stack>
        </form>
      </Stack>
    </Container>
  );
}

export default WebsiteSettingsPage;
